import { Location } from '@angular/common';
import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';

import { CheckPlatformService } from '@services/helper/check-platform.service';
import { UserPermissionService } from '@services/helper/user-permission.service';

/**
 * Unified guard for roles and permissions
 * Usage in routes:
 * canActivate: [rolePermissionGuard]
 * data: {
 *   roles: ['admin', 'manager'],
 *   permissions: ['VIEW_DASHBOARD', 'EDIT_CONTENT'],
 *   requireAllPermissions: true // optional, default false
 * }
 */
export const rolePermissionGuard: CanActivateFn = (route) => {
  // Get PermissionService instance
  const permissionService = inject(UserPermissionService);
  // Get CheckPlatformService instance
  const _checkPlatformService = inject(CheckPlatformService);
  // Get Location instance
  const location = inject(Location);
  // const router = inject(Router);

  // Get required roles from route data
  // If no roles are defined or roles array is empty, allow access
  const requiredRoles = route.data?.['roles'] as string[] | undefined;

  // Get required permissions from route data
  // If no permissions are defined or permissions array is empty, allow access
  const requiredPermissions = route.data?.['permissions'] as string[] | undefined;

  // Get requireAllPermissions flag from route data
  // If requireAllPermissions is not defined or is false, allow access
  const requireAllPermissions = (route.data?.['requireAllPermissions'] as boolean) ?? false;

  // If not browser (SSR), allow access
  // This is because the permissions and roles are only checked in the browser, not in the server
  // If we don't allow access here, the user will get stuck in an infinite loop of redirects between the server and the browser
  if (!_checkPlatformService.isBrowser()) {
    return true;
  }

  /* `/me` is fetched by the header, which renders *after* guards, so on a cold
   * page load (a deep link, a refresh, opening a bookmark) landing directly on
   * a route this guard protects — with no earlier `dashboardAccessGuard`-guarded
   * route in the same session to have already done this — role/permission state
   * is still empty and every visitor looks unprivileged. Restore what was
   * persisted at login before checking anything (same fix as
   * dashboardAccessGuard, FEC-1171; this guard just never got it). */
  if (!permissionService.hasLoadedPermissions()) permissionService.initializePermissions();

  // If no roles and no permissions are defined, allow access
  // If no roles and no permissions are defined, allow access
  // If user doesn't have required role or permission, deny access and go back to previous page
  if (
    // If no roles are defined or roles array is empty, allow access
    (!requiredRoles || requiredRoles.length === 0) &&
    // If no permissions are defined or permissions array is empty, allow access
    (!requiredPermissions || requiredPermissions.length === 0)
  ) {
    return true;
  }

  // Check if user is authenticated
  // if (!permissionService.isAuthenticated()) {
  //   router.navigate(['/auth/login']);
  //   return false;
  // }

  // Check roles
  // If user does not have required role, deny access and go back to previous page
  if (requiredRoles && requiredRoles.length > 0) {
    const currentRole = permissionService.getPersistedRoleName();
    if (!currentRole || !requiredRoles.includes(currentRole)) {
      // Alert user that they do not have permission to access this page
      alert('You do not have permission to access this page.');
      // Go back to previous page
      _checkPlatformService.isBrowser() && location.back();
      // Deny access
      return false;
    }
  }

  // Check permissions
  if (requiredPermissions && requiredPermissions.length > 0) {
    let hasPermission = false;

    if (requireAllPermissions) {
      // User must have ALL specified permissions
      hasPermission = requiredPermissions.every((p) => permissionService.hasPermission(p));
    } else {
      // User must have at least ONE of the specified permissions
      hasPermission = requiredPermissions.some((p) => permissionService.hasPermission(p));
    }

    // If user does not have required permissions, go back to previous page and deny access
    if (!hasPermission) {
      // Go back to previous page
      _checkPlatformService.isBrowser() && window.history.back();
      // Deny access by returning false from the guard
      return false;
    }
  }

  return true;
};
